import { inject, injectable } from "inversify";
import * as THREE from "three";
import { CSS2DRenderer } from "three/examples/jsm/renderers/CSS2DRenderer.js";
import { DomElementsService, Logger } from "@designboticteam/core";
import { CameraService } from "../../stage/CameraService";
import { SceneService } from "../../stage/SceneService";
import type { IRendererService } from "./IRendererService";

@injectable()
class DefaultRendererService implements IRendererService {
	readonly serviceName = "🖌️ DefaultRendererService";
	private logger = new Logger(`🎬 WORKSPACE 3D | ${this.serviceName}`, {
		color: "#ffffff",
		backgroundColor: "#0066cc",
	});

	#renderer3D: THREE.WebGLRenderer | null = null;
	#renderer2D: CSS2DRenderer | null = null;
	private readonly renderLoopListeners = new Set<() => void>();

	public get renderer3D() {
		if (!this.#renderer3D) {
			throw new Error("Renderer not initialized. Call createRenderer() first.");
		}

		return this.#renderer3D;
	}

	public get renderer2D() {
		if (!this.#renderer2D) {
			throw new Error("2D renderer not initialized. Call createRenderer() first.");
		}

		return this.#renderer2D;
	}

	constructor(
		@inject(DomElementsService) private domElementsService: DomElementsService,
		@inject(SceneService) private sceneService: SceneService,
		@inject(CameraService) private cameraService: CameraService,
	) {
		this.logger.log("constructed");
	}

	public initialize(): void {
		if (this.domElementsService.containerElement.style.position === "") {
			this.domElementsService.containerElement.style.position = "relative";
		}

		this.#renderer3D = new THREE.WebGLRenderer({
			antialias: true,
			alpha: true,
			logarithmicDepthBuffer: true,
			powerPreference: "high-performance",
		});

		this.#renderer3D.setPixelRatio(window.devicePixelRatio);
		this.#renderer3D.shadowMap.enabled = true;
		this.#renderer3D.autoClear = true;
		this.#renderer2D = new CSS2DRenderer();
		Object.assign(this.#renderer2D.domElement.style, {
			position: "absolute",
			top: "0",
			left: "0",
			pointerEvents: "none",
			overflow: "hidden",
			zIndex: "1",
		} satisfies Partial<CSSStyleDeclaration>);
		this.updateSize();

		this.domElementsService.containerElement.append(this.#renderer3D.domElement);
		this.domElementsService.containerElement.append(this.#renderer2D.domElement);

		this.renderer3D.setAnimationLoop(this.loop.bind(this));
		this.logger.log("initialized");
	}

	public dispose() {
		this.domElementsService.containerElement.removeChild(this.renderer2D.domElement);
		this.domElementsService.containerElement.removeChild(this.renderer3D.domElement);
		this.renderer3D.dispose();
		this.#renderer3D = null;
		this.#renderer2D = null;
	}

	public setPixelRatio(value: number) {
		this.renderer3D.setPixelRatio(value);
		this.updateSize();
	}

	public addRenderLoopListener(listener: () => void): void {
		this.renderLoopListeners.add(listener);
	}

	public removeRenderLoopListener(listener: () => void): void {
		this.renderLoopListeners.delete(listener);
	}

	private render() {
		this.sceneService.update();
		this.renderer3D.render(this.sceneService.scene, this.cameraService.camera);
		this.renderer2D.render(this.sceneService.scene, this.cameraService.camera);
	}

	private updateSize() {
		const { width, height } = this.domElementsService.containerElement.getBoundingClientRect();
		this.renderer3D.setSize(width, height);
		this.renderer2D.setSize(width, height);
		this.cameraService.camera.aspect = width / height;
		this.cameraService.camera.updateProjectionMatrix();
	}

	private loop(_time: DOMHighResTimeStamp, _frame: XRFrame) {
		for (const listener of this.renderLoopListeners) {
			listener();
		}

		this.render();
	}
}

export { DefaultRendererService };
